import {
  IMatchCardDataBlock,
  IMatchDataBlockLayout,
  MatchCardDataBlock,
} from '@sky-it-livedata/livedata-sports-design-system';
import { MatchCardQuery, MatchCardSubscription } from "./types";
import { graphql } from "appsync";
import queryTransformer from "./transformers/query";
import subscriptionTrasformer from "./transformers/subscription";
import { useSubscription } from '../../hooks/useSubscription';
import { useEffect, useState, useRef } from 'react';
import { execQuery } from '../../utils/query';
import { isClient } from "../../utils/client";

const queryDocument = graphql("query GetMatchCard($matchId: ID!) {\n  getMatchCard(matchId: $matchId) {\n    matchId\n    date\n    status\n    competitionPhase\n    competitionPhaseRound\n    competitionPhaseWinnerTeamId\n    matchDetailUrl\n    competition {\n      id\n      name\n    }\n    home {\n      id\n      logoUrl\n      negativeLogoUrl\n      homepageUrl\n      goal\n      name\n      seoName\n      overtimeGoal\n      penaltiesGoal\n      regularTimeGoal\n      shootoutGoal\n      totalGoalLegs\n    }\n    away {\n      id\n      logoUrl\n      negativeLogoUrl\n      homepageUrl\n      goal\n      name\n      seoName\n      overtimeGoal\n      penaltiesGoal\n      regularTimeGoal\n      shootoutGoal\n      totalGoalLegs\n    }\n  }\n}");
const subscriptionDocument = graphql("subscription MatchCardSubscription($matchId: ID!) {\n  onUpdateMatchCardByMatchId(matchId: $matchId) {\n    matchId\n    date\n    status\n    competitionPhase\n    competitionPhaseRound\n    competitionPhaseWinnerTeamId\n    matchDetailUrl\n    competition {\n      id\n      name\n    }\n    home {\n      id\n      logoUrl\n      negativeLogoUrl\n      homepageUrl\n      goal\n      name\n      seoName\n      overtimeGoal\n      penaltiesGoal\n      regularTimeGoal\n      shootoutGoal\n      totalGoalLegs\n    }\n    away {\n      id\n      logoUrl\n      negativeLogoUrl\n      homepageUrl\n      goal\n      name\n      seoName\n      overtimeGoal\n      penaltiesGoal\n      regularTimeGoal\n      shootoutGoal\n      totalGoalLegs\n    }\n  }\n}");

export const DEFAULT_LAYOUT: IMatchDataBlockLayout = {
  colorSchema: 'dark-light',
  fontSize: '16--14',
  logoSize: '30',
  size: 'small',
};

interface MatchCardWidgetProps {
  layout?: Partial<IMatchDataBlockLayout>;
  tag?: string;
  version?: string;
}

const MatchCardWidget = (props: MatchCardWidgetProps) => {
  const [initialData, setInitialData] = useState<IMatchCardDataBlock | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);

  const getMatchIdFromDOM = (): string => {
    if (!isClient() || !containerRef.current) return '';
    
    let parent = containerRef.current.parentElement;
    while (parent) {
      if (parent.tagName === 'LD-FOOTBALL-MATCH-CARD') {
        const variables = parent.getAttribute("variables");
        if (variables) {
          return JSON.parse(variables).matchId;
        }
      }
      parent = parent.parentElement;
    }
    return '';
  };

  const getLayoutFromDOM = () => {
    if (!isClient() || !containerRef.current) return DEFAULT_LAYOUT;
    
    let parent = containerRef.current.parentElement;
    while (parent) {
      if (parent.tagName === 'LD-FOOTBALL-MATCH-CARD') {
        const layout = parent.getAttribute("layout");
        if (layout) {
          const layoutObj = JSON.parse(layout);
          return { ...DEFAULT_LAYOUT, ...layoutObj, ...props.layout };
        }
      }
      parent = parent.parentElement;
    }
    return { ...DEFAULT_LAYOUT, ...props.layout };
  };

  const matchId = getMatchIdFromDOM();
  const mergedLayout = getLayoutFromDOM();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const queryData = await execQuery(queryDocument, { matchId }) as MatchCardQuery;
        const transformed = queryTransformer(queryData);
        setInitialData(transformed);
        
      } catch (error) {
        console.error('Failed to fetch match card', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (matchId) {
      fetchData();
    } else {
      setIsLoading(false);
    }
  }, [matchId]);

  const updatedProps = useSubscription<
    IMatchCardDataBlock | null,
    MatchCardSubscription,
    typeof subscriptionDocument
  >({
    initialData: initialData,
    graphQLDocument: subscriptionDocument,
    variables: { matchId },
    transform: subscriptionTrasformer,
    mergeData: true,
    enabled: !!matchId,
  }) as IMatchCardDataBlock | null;

  const dataToRender = updatedProps || initialData;
  
  return (
    <div ref={containerRef}>
      <MatchCardDataBlock
        match={dataToRender?.match || ({} as IMatchCardDataBlock['match'])}
        isLoading={isLoading || !dataToRender?.match}
        layout={mergedLayout}
      />
    </div>
  );
};

export const getServerProps = async () => ({});

export default MatchCardWidget;
